/** @jsx jsx */
import { jsx, ThemeUIStyleObject } from "theme-ui";
import React, { useEffect, useRef, useState } from "react";
import { PrimitiveProps } from "@styles/types";
import { Box } from "@shared-components/Box/Box";
import { motion } from "framer-motion";
import { millisToSeconds } from "@styles/utils/millisToSeconds";
import transitions from "@styles/design-tokens/09.transitions/transitions";
import opacities from "@styles/design-tokens/07.opacities/opacities";
import { useInView } from "react-intersection-observer";
import LazyLoad from "react-lazyload";
import { Icon } from "@shared-components/Icon/Icon";
import PlayIcon from "@svg-icons/play-icon.component.svg";
import PauseIcon from "@svg-icons/pause-icon.component.svg";
import MuteIcon from "@svg-icons/mute-icon.component.svg";
import MutedIcon from "@svg-icons/muted-icon.component.svg";
import FullscreenIcon from "@svg-icons/fullscreen-icon.component.svg";
import LoadingIcon from "@svg-icons/leaf-loader.component.svg";
import { useVideoPlayer } from "./VideoPlayerContext";
import "@styles/videoIOSreset.css";
import { Text } from "@shared-components/Text/Text";
import { useDictionaryContext } from "../../../context/dictionary/useDictionaryContext";
import { useSiteContext } from "../../../context/site/useSiteContext";
import { DICTIONARY_MAP } from "../../../carlsberg-headless/dictionary-map";

interface VideoPlayerProps
  extends PrimitiveProps,
    React.ComponentPropsWithoutRef<"video"> {
  src: string;
  extention: string;
  noSound?: boolean;
  videoTitle?: string;
}

const VideoPlayerStyles: ThemeUIStyleObject = {
  position: "relative",
  height: "100%",
  button: {
    fill: "darkGreen",
  },
};
const VideoPlayerElementStyles: ThemeUIStyleObject = {
  height: "100%",
  width: "100%",
  objectFit: "contain",
  clipPath: "inset(-1px -1px -1px -1px)",
};
const VideoPlayerButtonStyles: ThemeUIStyleObject = {
  position: "absolute",
  top: "0px",
  left: "0px",
  width: "60px",
  height: "60px",
  border: 0,
  background: "transparent",
  cursor: "pointer",
  padding: 0,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  ":hover": {
    span: {
      backgroundColor: "darkGreen",
    },
    svg: {
      fill: "white",
    },
  },
  span: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "white",
    borderRadius: "50%",
    width: "40px",
    height: "40px",
    transition: "all 0.2s linear",
  },
  svg: {
    width: "20px",
    height: "20px",
  },
};
const VideoPlayerSoundButtonStyles: ThemeUIStyleObject = {
  ...VideoPlayerButtonStyles,
  ...{
    top: "0px",
    right: "0px",
    left: "auto",
  },
};

const VideoPlayerFullscreenButtonStyles: ThemeUIStyleObject = {
  ...VideoPlayerButtonStyles,
  ...{
    top: "auto",
    bottom: "0px",
    right: "0px",
    left: "auto",
  },
};

const FullscreenVideoStyle: ThemeUIStyleObject = {
  position: "fixed",
  top: "0",
  left: "0",
  width: "100%",
  height: "100%",
  zIndex: "100",
};

const VideoPlayer: React.FC<VideoPlayerProps> = ({
  sx,
  src,
  extention,
  noSound,
  videoTitle,
  ...props
}) => {
  const { get } = useDictionaryContext();
  const { currentCulture } = useSiteContext();

  // const ua = window.navigator.userAgent;
  // const iOS = !!ua.match(/iPad/i) || !!ua.match(/iPhone/i);
  // const webkit = !!ua.match(/WebKit/i);
  // const iOSSafari = iOS && webkit && !ua.match(/CriOS/i);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const videoWrapRef = useRef<HTMLDivElement | null>(null);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [isPlaying, setPlaying] = useState<boolean>(false);
  const [isFullScreen, setFullScreen] = useState<boolean>(false);
  const inviewTrackingRef = useRef<boolean>(false);

  const {
    someIsPlaying,
    setSomePlaying,
    isMuted,
    setIsMuted,
    playingVideoRef,
    setPlayingVideoRef,
  } = useVideoPlayer();

  const [ref, inView] = useInView({
    triggerOnce: false,
  });

  const toggleFullScreen = () => {
    if (videoWrapRef.current) {
      if (!isFullScreen) {
        if (videoWrapRef.current.requestFullscreen) {
          videoWrapRef.current.requestFullscreen().then(() => {
            setFullScreen(true);
          });
          // @ts-ignore-next-line
        } else if (videoWrapRef.current.webkitRequestFullscreen) {
          // @ts-ignore-next-line
          videoWrapRef.current.webkitRequestFullscreen();
        }
      } else {
        if (document.exitFullscreen) {
          document.exitFullscreen().then(() => {
            setFullScreen(false);
          });
          // @ts-ignore-next-line
        } else if (document.webkitRequestFullscreen) {
          // @ts-ignore-next-line
          document.webkitRequestFullscreen().then(() => {
            setFullScreen(false);
          });
        }
      }
    }
  };

  document.addEventListener("fullscreenchange", () => {
    if (!document.fullscreenElement) {
      setFullScreen(false);
    }
  });

  const onVideoPlayClick = (e: any) => {
    e.preventDefault();
    if (!videoRef.current) return;

    if (isPlaying) {
      videoRef.current.pause();
      setPlaying(false);
      setSomePlaying(false);
    } else {
      playingVideoRef?.pause();
      videoRef.current.play();
      setPlaying(true);
      setSomePlaying(true);
      setPlayingVideoRef(videoRef.current);

      trackVideo();
    }
  };

  useEffect(() => {
    setPlaying(!videoRef.current?.paused);
  }, [videoRef.current?.paused]);

  const onVideoSoundClick = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  useEffect(() => {
    if (videoRef.current && videoRef.current !== playingVideoRef && isPlaying) {
      videoRef.current.pause();
    }
  }, [playingVideoRef]);

  useEffect(() => {
    if (inView) {
      if (playingVideoRef) {
        playingVideoRef.pause();
        playingVideoRef.currentTime = 0;
      }

      if (
        someIsPlaying &&
        !isLoading &&
        videoWrapRef?.current?.requestFullscreen
      ) {
        videoRef?.current?.play();
      }
    }
  }, [inView]);

  function trackVideo() {
    if (typeof window !== "undefined") {
      //@ts-ignore-next-line
      window.dataLayer.push({
        event: "video_play",
        video_title: videoTitle,
        video_url: src,
      });
    }
  }

  useEffect(() => {
    if (inView && isPlaying && !isLoading && !inviewTrackingRef.current) {
      trackVideo();
      inviewTrackingRef.current = true;
    }
  }, [inView, isPlaying, isLoading]);

  const onCanPlayThrough = () => {
    videoRef?.current?.style.setProperty("background-color", "white");
    setLoading(false);
  };

  return (
    <Box
      sx={{
        ...VideoPlayerStyles,
        ...sx,
      }}
    >
      {isLoading && (
        <div
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 100,
            textAlign: "center",
          }}
        >
          <Text
            sx={{
              color: "darkGreen",
              fontSize: "paragraphSmall",
              textTransform: "uppercase",
              marginBottom: "10px",
              fontWeight: "bold",
            }}
          >
            {get(
              DICTIONARY_MAP.digital_memories.digital_memories_loading_text,
              currentCulture
            )}
          </Text>
          <Icon
            svg={LoadingIcon}
            sx={{
              width: "20px",
              height: "20px",
              margin: "0 auto",
              fill: "darkGreen",
              transformOrigin: "50% 45%",
              animation: "rotation 0.75s infinite linear",
            }}
          />
        </div>
      )}
      <LazyLoad height={props.height} once>
        <motion.div
          initial={{ opacity: opacities[0] }}
          animate={{ opacity: opacities[100] }}
          transition={{
            duration: millisToSeconds(transitions.durations["300"]),
          }}
        >
          <div ref={ref}>
            <div
              ref={videoWrapRef}
              sx={{ ...(isFullScreen && FullscreenVideoStyle) }}
            >
              <video
                ref={videoRef}
                sx={VideoPlayerElementStyles}
                autoPlay={someIsPlaying}
                loop
                muted={isMuted}
                {...(!videoWrapRef?.current?.requestFullscreen && {
                  playInline: true,
                })}
                onClick={onVideoPlayClick}
                onLoadStart={() => setLoading(true)}
                onPlay={() => {
                  setPlaying(true);
                  setPlayingVideoRef(videoRef.current);
                  setLoading(false);
                }}
                onPause={() => {
                  videoRef?.current?.removeAttribute("controls");
                  setPlaying(false);
                }}
                onLoadedData={onCanPlayThrough}
                {...props}
              >
                <source src={src} type="video/mp4" />
              </video>
              <button sx={VideoPlayerButtonStyles} onClick={onVideoPlayClick}>
                <span>
                  {isPlaying ? (
                    <Icon svg={PauseIcon} />
                  ) : (
                    <Icon svg={PlayIcon} />
                  )}
                </span>
              </button>
              {!noSound && (
                <button
                  sx={VideoPlayerSoundButtonStyles}
                  onClick={onVideoSoundClick}
                >
                  <span>
                    {isMuted ? (
                      <Icon svg={MutedIcon} />
                    ) : (
                      <Icon svg={MuteIcon} />
                    )}
                  </span>
                </button>
              )}

              {videoWrapRef?.current?.requestFullscreen && (
                <button
                  sx={VideoPlayerFullscreenButtonStyles}
                  onClick={toggleFullScreen}
                >
                  <span>
                    <Icon svg={FullscreenIcon} />
                  </span>
                </button>
              )}
            </div>
          </div>
        </motion.div>
      </LazyLoad>
    </Box>
  );
};

VideoPlayer.defaultProps = {};

export { VideoPlayer };
